import { DateTime } from 'luxon';
import { getFormatCurrency, toNumber, roundMoney } from '@/utils/CurrencyUtil';

const ISO = 'yyyy-MM-dd';

// Tope del backend (DashboardRange.MAX_DAYS): un rango mayor responde 400.
export const MAX_DAYS = 366;
export const DEFAULT_CURRENCY = 'MXN';

/**
 * Rango `yyyy-MM-dd` que se manda al backend. Sin las dos fechas (o con un rango mayor al tope) se acota a los ultimos
 * `MAX_DAYS`; `clamped` avisa que no es el rango que eligio la persona.
 */
export function dashboardRange(start, end, today = DateTime.now()) {
    const fallback = { start: today.minus({ days: MAX_DAYS - 1 }).toFormat(ISO), end: today.toFormat(ISO), clamped: true };
    if (!start || !end) return fallback;
    const from = DateTime.fromFormat(start, ISO);
    const to = DateTime.fromFormat(end, ISO);
    if (!from.isValid || !to.isValid || from > to) return fallback;
    if (Math.round(to.diff(from, 'days').days) + 1 > MAX_DAYS) {
        return { start: to.minus({ days: MAX_DAYS - 1 }).toFormat(ISO), end, clamped: true };
    }
    return { start, end, clamped: false };
}

const list = (rows) => (Array.isArray(rows) ? rows : []);

/** Monedas presentes en las filas: MXN primero, el resto en orden alfabetico. */
export function currencyCodes(...lists) {
    const codes = new Set();
    lists.forEach((rows) => list(rows).forEach((row) => row.codigoMoneda && codes.add(row.codigoMoneda)));
    return [...codes].sort((a, b) => {
        if (a === DEFAULT_CURRENCY) return -1;
        if (b === DEFAULT_CURRENCY) return 1;
        return a < b ? -1 : 1;
    });
}

/** La moneda elegida si sigue existiendo; si no, MXN; si tampoco, la primera. */
export function pickCurrency(codes, selected) {
    if (codes.includes(selected)) return selected;
    if (codes.includes(DEFAULT_CURRENCY)) return DEFAULT_CURRENCY;
    return codes[0] || DEFAULT_CURRENCY;
}

export const forCurrency = (rows, code) => list(rows).filter((row) => row.codigoMoneda === code);

/** Total, por cobrar, tickets y ticket promedio de una moneda a partir de `resumen`. */
export function summaryFor(resumen, code) {
    const row = forCurrency(resumen, code)[0];
    if (!row) return { total: 0, owed: 0, tickets: 0, average: 0 };
    const total = toNumber(row.total, 0);
    const tickets = toNumber(row.documentos, 0);
    return { total, owed: toNumber(row.saldo, 0), tickets, average: tickets ? roundMoney(total / tickets) : 0 };
}

/** `$1,500.00` en pesos; `USD $1,500.00` en otra moneda (nunca se mezclan sin decir cual es). */
export function formatMoney(value, code = DEFAULT_CURRENCY) {
    const prefix = !code || code === DEFAULT_CURRENCY ? '$' : `${code} $`;
    return getFormatCurrency(toNumber(value, 0), prefix);
}

const WEEKDAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * `2026-10-09` -> `09/oct` (el mes abreviado deja claro el día en rangos largos). Con `weekdays`, dos líneas para el
 * eje (Chart.js las apila): `['Viernes', '09/oct']`.
 */
export function dayLabel(iso, weekdays = false) {
    const match = /^\d{4}-(\d{2})-(\d{2})/.exec(String(iso || ''));
    if (!match || !MONTHS[Number(match[1]) - 1]) return weekdays ? [] : '';
    const short = `${match[2]}/${MONTHS[Number(match[1]) - 1]}`;
    if (!weekdays) return short;
    const day = DateTime.fromFormat(String(iso).slice(0, 10), ISO);
    return day.isValid ? [WEEKDAYS[day.weekday - 1], short] : short;
}

/**
 * Los días del rango (`yyyy-MM-dd`, del primero al último) cuando el periodo es de una semana o menos, para rotular
 * las gráficas por día con Lunes, Martes... y mostrar también los días sin movimiento. `null` si el rango es más
 * largo (o no es válido): ahí las gráficas siguen con `dd/mes` (09/oct) y solo los días con datos.
 */
export function weekDays(start, end) {
    const from = DateTime.fromFormat(String(start || ''), ISO);
    const to = DateTime.fromFormat(String(end || ''), ISO);
    if (!from.isValid || !to.isValid || from > to) return null;
    const count = Math.round(to.diff(from, 'days').days) + 1;
    if (count > 7) return null;
    return Array.from({ length: count }, (_, i) => from.plus({ days: i }).toFormat(ISO));
}

/** `{ labels, data }` de Chart.js para una moneda. `labelKey`: clave/producto/nombre; `valueKey`: monto/saldo. */
export function chartFor(rows, code, { labelKey = 'clave', valueKey = 'monto' } = {}) {
    const filtered = forCurrency(rows, code);
    return {
        labels: filtered.map((row) => row[labelKey]),
        data: filtered.map((row) => toNumber(row[valueKey], 0))
    };
}

/**
 * Importe por dia de una moneda: etiquetas `09/oct`, en el orden en que llegan (el backend ya las ordena). Con `week`
 * (la lista de `weekDays`) salen todos los días de la semana, con su nombre, y 0 donde no hubo ventas.
 */
export function dayChartFor(rows, code, week = null) {
    const filtered = forCurrency(rows, code);
    if (!week) {
        return { labels: filtered.map((row) => dayLabel(row.dia)), data: filtered.map((row) => toNumber(row.monto, 0)) };
    }
    const totals = new Map();
    filtered.forEach((row) => totals.set(row.dia, (totals.get(row.dia) || 0) + toNumber(row.monto, 0)));
    return { labels: week.map((day) => dayLabel(day, true)), data: week.map((day) => totals.get(day) || 0) };
}

const pieces = (row) => toNumber(row.cantidad, 0);

/** Total de piezas de las filas `{ producto, dia, cantidad }`. */
export const sumPieces = (rows) => list(rows).reduce((sum, row) => sum + pieces(row), 0);

/** Piezas por producto, de mayor a menor (para la dona). */
export function piecesByProduct(rows) {
    const totals = new Map();
    list(rows).forEach((row) => totals.set(row.producto, (totals.get(row.producto) || 0) + pieces(row)));
    const entries = [...totals.entries()].sort((a, b) => b[1] - a[1]);
    return { labels: entries.map(([name]) => name), data: entries.map(([, total]) => total) };
}

/**
 * Piezas por dia con un producto por serie: `labels` son los dias (`09/oct`, del mas antiguo al mas reciente) y cada
 * dataset es un producto con una cifra por dia (0 si ese dia no se movio). Los productos van de mayor a menor total.
 */
export function piecesByDay(rows, week = null) {
    const days = week || [...new Set(list(rows).map((row) => row.dia).filter(Boolean))].sort();
    const order = piecesByProduct(rows).labels;
    const perProduct = new Map(order.map((name) => [name, new Map()]));
    list(rows).forEach((row) => {
        const byDay = perProduct.get(row.producto);
        byDay.set(row.dia, (byDay.get(row.dia) || 0) + pieces(row));
    });
    return {
        labels: days.map((day) => dayLabel(day, Boolean(week))),
        datasets: order.map((name) => ({ label: name, data: days.map((day) => perProduct.get(name).get(day) || 0) }))
    };
}

/**
 * Vendidas contra merma, dia por dia: dos series (`tone` pinta la de merma en rojo) sobre la union de los dias de
 * ambas listas, 0 donde una de las dos no se movio.
 */
export function salesVsLoss(soldRows, lostRows, week = null) {
    const days = week || [...new Set([...list(soldRows), ...list(lostRows)].map((row) => row.dia).filter(Boolean))].sort();
    const perDay = (rows) => {
        const totals = new Map();
        list(rows).forEach((row) => totals.set(row.dia, (totals.get(row.dia) || 0) + pieces(row)));
        return days.map((day) => totals.get(day) || 0);
    };
    return {
        labels: days.map((day) => dayLabel(day, Boolean(week))),
        datasets: [
            { label: 'Vendidas', tone: 'success', data: perDay(soldRows) },
            { label: 'Merma', tone: 'error', data: perDay(lostRows) }
        ]
    };
}

/** Vendidas contra merma por sucursal (filas `{ clave, cantidad }`), de mayor a menor venta. */
export function salesVsLossByBranch(soldRows, lostRows) {
    const lost = new Map(list(lostRows).map((row) => [row.clave, pieces(row)]));
    const sold = new Map(list(soldRows).map((row) => [row.clave, pieces(row)]));
    const labels = [...new Set([...sold.keys(), ...lost.keys()])].sort((a, b) => (sold.get(b) || 0) - (sold.get(a) || 0));
    return {
        labels,
        datasets: [
            { label: 'Vendidas', tone: 'success', data: labels.map((name) => sold.get(name) || 0) },
            { label: 'Merma', tone: 'error', data: labels.map((name) => lost.get(name) || 0) }
        ]
    };
}

/** Dinero vendido de productos y de servicios en una moneda, listo para una dona (omite lo que vale 0). */
export function kindChartFor(rows, code) {
    const mine = forCurrency(rows, code);
    const money = (servicio) => mine.filter((row) => Boolean(row.servicio) === servicio).reduce((sum, row) => sum + toNumber(row.monto, 0), 0);
    const entries = [['Productos', money(false)], ['Servicios', money(true)]].filter(([, total]) => total > 0);
    return { labels: entries.map(([name]) => name), data: entries.map(([, total]) => total) };
}

const FILTER_LABELS = { sucursal: 'Sucursal', producto: 'Producto' };

/**
 * Chips de un widget para los filtros del tablero, para que nadie lea un total filtrado donde no lo es.
 *
 * `scope` es lo que el widget declara (`filterScope` del mixin): `sucursal` / `producto` (si los respeta), `reasons`
 * (por qué no aplica uno) y `partial` (cambios que trae un filtro que sí aplica, p. ej. un KPI que se oculta).
 * Devuelve `applied` (`{ label }`: el filtro se aplicó) e `ignored` (`{ label, reason }`: no se aplicó, o se aplicó
 * con una salvedad). Sin filtros elegidos, ambas listas vienen vacías.
 */
export function filterBadges(scope, ids, names) {
    const applied = [];
    const ignored = [];
    ['sucursal', 'producto'].forEach((key) => {
        if (!ids[`${key}Id`]) return;
        if (scope[key]) {
            applied.push({ label: `${FILTER_LABELS[key]}: ${names[`${key}Nombre`] || 'elegido'}` });
            ((scope.partial && scope.partial[key]) || []).forEach((note) => ignored.push(note));
        } else {
            ignored.push({
                label: `${FILTER_LABELS[key]}: no aplica`,
                reason: (scope.reasons && scope.reasons[key]) || 'Este dato no se puede dividir con ese filtro.'
            });
        }
    });
    return { applied, ignored };
}

/** Inventario: un producto esta bajo minimo si tiene minimo configurado y la existencia no lo alcanza. */
export const isLowStock = (row) => toNumber(row.minStock, 0) > 0 && toNumber(row.existencia, 0) < toNumber(row.minStock, 0);

/**
 * Opciones de Chart.js para importes de una moneda (ejes y tooltips). Son objetos nuevos por llamada: quien las use
 * debe cachearlas (computed por moneda), porque `ChartCanvas` recrea la grafica cuando `options` cambia.
 */
export function moneyChartOptions(code = DEFAULT_CURRENCY) {
    const value = (item, data) => data.datasets[item.datasetIndex].data[item.index];
    const axis = { ticks: { beginAtZero: true, callback: (tick) => formatMoney(tick, code).replace(/\.00$/, '') } };
    const tooltips = { mode: 'index', intersect: false, callbacks: { label: (item, data) => formatMoney(value(item, data), code) } };
    return {
        vertical: { legend: { display: false }, scales: { yAxes: [axis] }, tooltips },
        horizontal: { legend: { display: false }, scales: { xAxes: [axis] }, tooltips },
        donut: {
            tooltips: {
                callbacks: { label: (item, data) => `${data.labels[item.index]}: ${formatMoney(value(item, data), code)}` }
            }
        }
    };
}
